/**
 * Butterfly drawings — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_doodles`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const butterflyDrawingsPack: ToilePack = {
    slug: 'butterfly-drawings',
    category: 'coloring',
    name: 'Butterfly drawings',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'monarch', label: 'Monarch', subject: 'a monarch butterfly seen from above, wings spread, the veins drawn as bold lines and white spots along the wing edges'},
        {key: 'swallowtail', label: 'Swallowtail', subject: 'a swallowtail butterfly with long tails on its hind wings, striped bands across the wings and eyespots near the tails'},
        {key: 'garden', label: 'On a flower', subject: 'a butterfly with folded wings resting on a daisy, a second smaller butterfly flying above it'},
    ],
    styles: [
        {key: 'outline', label: 'Bold outline', style: 'a bold black outline with clear wing cells and simple patterns, made for coloring', palette: 'Black ink only, no shading, no gray tones.', framing: 'The butterfly centered on the page with a wide white margin.'},
        {key: 'specimen', label: 'Specimen plate', style: 'a fine-line scientific plate, symmetric wings with every vein and scale pattern drawn precisely', palette: 'Black ink only.', framing: 'The butterfly centered on the page with a wide white margin.'},
        {key: 'leaded', label: 'Stained glass', style: 'thick black leaded lines dividing the wings into separate glass-like cells', palette: 'Black ink only, no fills.', framing: 'The butterfly centered on the page with a wide white margin.'},
    ],
    directions: ['a moth', 'dragonfly', 'with flowers'],
    directionHint: 'try: a moth',
    seo: {
        title: 'Butterfly Drawings: Monarch, Swallowtail, Garden',
        metaDescription: 'Nine butterfly drawings, a monarch, a swallowtail and a butterfly on a daisy, in bold outline, specimen plate and stained glass.',
        h1: 'Butterfly drawings: monarch, swallowtail, butterfly on a flower',
        tagline: 'Butterflies seen from above or at rest, with the wing patterns drawn clearly.',
        intro: 'Butterfly drawings are butterflies drawn for coloring, for science notebooks and as decoration. These nine pages show butterflies with their wing patterns drawn clearly: a monarch, a swallowtail, and a butterfly resting on a daisy. Each comes as a bold outline to color, a fine-line specimen plate, or leaded lines dividing the wings into cells. For a different insect, type it in the direction box.',
        useCases: [
            'A science notebook page',
            'A coloring page for a child',
            'A decoration for a classroom wall',
            'A pattern study for a craft',
        ],
        faq: [
            {q: 'Are all the butterflies seen from above?', a: 'The monarch and the swallowtail are seen from above with wings spread. The daisy reading shows one butterfly with folded wings resting on a flower.'},
            {q: 'What is the specimen plate style?', a: 'A fine-line drawing of symmetric wings, with the veins and scale patterns drawn in detail, in black ink only.'},
            {q: 'Can I ask for a different insect?', a: 'Yes. Type it in the direction box, such as a moth, a dragonfly or with flowers. A typed insect is a request, so the result may differ from the samples.'},
        ],
    },
}
